import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { App, Button, Card, DatePicker, Drawer, Form, Input, Modal, Select, Skeleton, type MenuProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import type { CreateTaskInput, TaskItem, TaskSectionItem } from '@crm/shared';
import {
  useCreateSection,
  useCreateTask,
  useDeleteSection,
  useRenameSection,
  useReorderSections,
  useReorderTasks,
  useTaskWorkspace,
  useUpdateTask,
} from '../../features/tasks/api';
import { buildTree, compareTasks, DUE_BUCKETS, dueBucket, dueForBucket, dueFromDay, EMPTY_FILTERS, filterFlat, type TaskFilters, type TaskSort } from '../../features/tasks/view';
import { useProjectScope } from '../../features/projects/scope';
import { useAuth } from '../../auth/AuthProvider';
import { errorMessage } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { ProjectSelect, ProjectTag } from '../../components/ProjectSelect';
import { LoadError } from '../../components/states';
import { TaskBoardView, TaskListView, useTaskSelection, type TaskGroup, type TaskViewProps } from './TaskViews';
import { TaskCalendarView } from './TaskCalendarView';
import { TaskBulkBar, TaskToolbar, useStoredState, type TaskStatus, type TaskView } from './TaskToolbar';
import { TaskDetailPanel } from './TaskDetailPanel';
import { AssigneeSelect, priorityOptions, useTaskUsers } from './TaskBits';
import './tasks.css';

type Mode = 'project' | 'mine';

/** The task drawer follows ?task=<id>, so the browser's back button closes it and the address can be shared. */
function useTaskDrawer() {
  const [params, setParams] = useSearchParams();
  const open = params.get('task');
  const set = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('task', id);
        else next.delete('task');
        return next;
      },
      { replace: !!open && !!id },
    );
  return { open, set };
}

function SectionModal({ open, onClose, defaultProject }: { open: boolean; onClose: () => void; defaultProject: string | null }) {
  const { message } = App.useApp();
  const scope = useProjectScope();
  const create = useCreateSection();
  const [form] = Form.useForm<{ name: string; projectId: string | null }>();
  return (
    <Modal
      open={open}
      title="Add section"
      okText="Add section"
      onCancel={onClose}
      confirmLoading={create.isPending}
      onOk={() => form.submit()}
      destroyOnHidden
      afterOpenChange={(o) => o && form.setFieldsValue({ name: '', projectId: defaultProject })}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) =>
          // the Project field is hidden when the header has a project, and onFinish only returns rendered fields
          create.mutateAsync({ name: v.name, projectId: v.projectId ?? defaultProject }).then(
            () => onClose(),
            (e) => message.error(errorMessage(e)),
          )
        }
      >
        <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Name the section' }]}>
          <Input autoFocus placeholder="e.g. To do, In progress, Waiting" maxLength={120} />
        </Form.Item>
        {scope.enabled && !scope.current && (
          <Form.Item name="projectId" label="Project" extra="Sections belong to one project.">
            <ProjectSelect />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

interface NewTaskValues {
  title: string;
  assigneeUserId: string | null;
  due: Dayjs | null;
  priority: CreateTaskInput['priority'];
  sectionId: string | null;
  projectId: string | null;
}

function NewTaskModal({ open, onClose, mode, sections, onCreated }: { open: boolean; onClose: () => void; mode: Mode; sections: TaskSectionItem[]; onCreated: (t: TaskItem) => void }) {
  const { message } = App.useApp();
  const { user } = useAuth();
  const scope = useProjectScope();
  const create = useCreateTask();
  const [form] = Form.useForm<NewTaskValues>();
  const projectId = Form.useWatch('projectId', form);
  const sectionOptions = sections.filter((s) => !scope.enabled || scope.current || s.projectId === (projectId ?? null));
  return (
    <Modal
      open={open}
      title="New task"
      okText="Create task"
      onCancel={onClose}
      confirmLoading={create.isPending}
      onOk={() => form.submit()}
      destroyOnHidden
      afterOpenChange={(o) =>
        o && form.setFieldsValue({ title: '', assigneeUserId: mode === 'mine' ? (user?.id ?? null) : null, due: null, priority: null, sectionId: null, projectId: scope.current?.id ?? null })
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) =>
          create
            .mutateAsync({
              title: v.title,
              assigneeUserId: v.assigneeUserId ?? null,
              dueAt: dueFromDay(v.due),
              priority: v.priority ?? null,
              ...(v.sectionId ? { sectionId: v.sectionId } : { projectId: v.projectId ?? scope.current?.id ?? null }),
            })
            .then(
              (t) => {
                onClose();
                onCreated(t);
              },
              (e) => message.error(errorMessage(e)),
            )
        }
      >
        <Form.Item name="title" label="Task name" rules={[{ required: true, whitespace: true, message: 'Name the task' }]}>
          <Input autoFocus maxLength={300} />
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Form.Item name="assigneeUserId" label="Assignee">
            <AssigneeSelect />
          </Form.Item>
          <Form.Item name="due" label="Due date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="priority" label="Priority">
            <Select allowClear placeholder="No priority" options={priorityOptions} />
          </Form.Item>
          {scope.enabled && !scope.current && (
            <Form.Item name="projectId" label="Project">
              <ProjectSelect onChange={() => form.setFieldValue('sectionId', null)} />
            </Form.Item>
          )}
          {sectionOptions.length > 0 && (
            <Form.Item name="sectionId" label="Section">
              <Select allowClear placeholder="No section" options={sectionOptions.map((s) => ({ value: s.id, label: s.name }))} />
            </Form.Item>
          )}
        </div>
      </Form>
    </Modal>
  );
}

/** Tasks of the selected project (or all projects), or My tasks: toolbar, list/board/calendar, bulk bar and drawer. */
function TaskWorkspacePage({ mode }: { mode: Mode }) {
  const { message, modal } = App.useApp();
  const { user } = useAuth();
  const scope = useProjectScope();
  const drawer = useTaskDrawer();
  const { names } = useTaskUsers();
  const prefix = `crm.tasks.${mode}`;
  const [view, setView] = useStoredState<TaskView>(`${prefix}.view`, 'list');
  const [sort, setSort] = useStoredState<TaskSort>(`${prefix}.sort`, mode === 'mine' ? 'due' : 'manual');
  const [status, setStatus] = useStoredState<TaskStatus>(`${prefix}.status`, 'open');
  const [filters, setFilters] = useState<TaskFilters>(EMPTY_FILTERS);
  const [sectionModal, setSectionModal] = useState(false);
  const [taskModal, setTaskModal] = useState(false);

  const ws = useTaskWorkspace({ scope: mode, status });
  const create = useCreateTask();
  const update = useUpdateTask(names);
  const reorder = useReorderTasks();
  const reorderSections = useReorderSections();
  const renameSection = useRenameSection();
  const deleteSection = useDeleteSection();

  const tasks = useMemo(() => ws.data?.tasks ?? [], [ws.data]);
  const showProject = scope.enabled && !scope.current;
  // across all projects, each project's sections stay together (projects by name, sections without one first)
  const sections = useMemo(() => {
    const list = ws.data?.sections ?? [];
    if (!showProject) return list;
    const name = (id: string | null) => (id ? (scope.byId(id)?.name ?? '\uffff') : '');
    return [...list].sort((a, b) => name(a.projectId).localeCompare(name(b.projectId)) || a.position - b.position);
  }, [ws.data, showProject, scope]);
  const parents = useMemo(() => new Map((ws.data?.parents ?? []).map((p) => [p.id, p.title])), [ws.data]);
  // My tasks: unsorted means by due date (manual order across days means little)
  const effectiveSort: TaskSort = mode === 'mine' && sort === 'manual' ? 'due' : sort;
  const tree = useMemo(() => buildTree(tasks, filters, effectiveSort, mode === 'mine'), [tasks, filters, effectiveSort, mode]);

  const fail = (e: unknown) => message.error(errorMessage(e));

  /** Swaps a section with its neighbour in the same project. */
  const siblings = (s: TaskSectionItem) => sections.filter((x) => x.projectId === s.projectId);
  const moveSection = (s: TaskSectionItem, by: -1 | 1) => {
    const ids = siblings(s).map((x) => x.id);
    const id = s.id;
    const i = ids.indexOf(id);
    const j = i + by;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorderSections.mutate(ids, { onError: fail });
  };
  const sectionMenu = (s: TaskSectionItem): MenuProps['items'] => {
    const index = siblings(s).indexOf(s);
    const last = siblings(s).length - 1;
    return [
    { key: 'rename', label: 'Rename section', onClick: () => renamePrompt(s) },
    { key: 'up', label: view === 'board' ? 'Move left' : 'Move up', disabled: index === 0, onClick: () => moveSection(s, -1) },
    { key: 'down', label: view === 'board' ? 'Move right' : 'Move down', disabled: index === last, onClick: () => moveSection(s, 1) },
    { type: 'divider' },
    {
      key: 'delete',
      label: 'Delete section',
      danger: true,
      onClick: () =>
        modal.confirm({
          title: `Delete the section "${s.name}"?`,
          content: 'Its tasks are kept and move to No section.',
          okText: 'Delete section',
          okButtonProps: { danger: true },
          onOk: () => deleteSection.mutateAsync(s.id).catch(fail),
        }),
    },
  ];
  };
  const renamePrompt = (s: TaskSectionItem) => {
    let name = s.name;
    modal.confirm({
      title: 'Rename section',
      icon: null,
      content: <Input defaultValue={s.name} autoFocus maxLength={120} aria-label="Section name" onChange={(e) => (name = e.target.value)} />,
      okText: 'Rename',
      onOk: () => (name.trim() && name.trim() !== s.name ? renameSection.mutateAsync({ id: s.id, name: name.trim() }).catch(fail) : undefined),
    });
  };

  const groups: TaskGroup[] = useMemo(() => {
    if (mode === 'mine') {
      const byBucket = new Map<string, TaskItem[]>();
      for (const t of tree.roots) {
        const b = dueBucket(t);
        byBucket.set(b, [...(byBucket.get(b) ?? []), t]);
      }
      return DUE_BUCKETS.filter((b) => !['overdue', 'later'].includes(b.key) || byBucket.has(b.key)).map((b) => ({
        key: b.key,
        title: b.title,
        tasks: byBucket.get(b.key) ?? [],
        canAdd: b.key !== 'overdue' && b.key !== 'later',
        droppable: b.key !== 'overdue',
      }));
    }
    const known = new Set(sections.map((s) => s.id));
    const bySection = new Map<string, TaskItem[]>();
    for (const t of tree.roots) {
      const key = t.sectionId && known.has(t.sectionId) ? t.sectionId : 'none';
      bySection.set(key, [...(bySection.get(key) ?? []), t]);
    }
    const none: TaskGroup = { key: 'none', title: 'No section', tasks: bySection.get('none') ?? [], canAdd: true };
    return [
      ...(none.tasks.length || !sections.length ? [none] : []),
      ...sections.map((s) => ({
        key: s.id,
        title: s.name,
        tasks: bySection.get(s.id) ?? [],
        badge: showProject ? <ProjectTag projectId={s.projectId} /> : undefined,
        menu: sectionMenu(s),
        canAdd: true,
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tree, sections, showProject, view]);

  const order = useMemo(() => groups.flatMap((g) => g.tasks.map((t) => t.id)), [groups]);
  const selection = useTaskSelection(order);

  const onMove: TaskViewProps['onMove'] = (taskId, toGroup, ids) => {
    if (mode === 'mine') {
      const task = tasks.find((t) => t.id === taskId);
      if (!task || dueBucket(task) === toGroup) return;
      const dueAt = dueForBucket(toGroup as Parameters<typeof dueForBucket>[0], task.dueAt);
      if (dueAt !== undefined) update.mutate({ id: taskId, dueAt }, { onError: fail });
      return;
    }
    reorder.mutate({ sectionId: toGroup === 'none' ? null : toGroup, ids }, { onError: fail });
  };

  const quickAdd = (group: TaskGroup, title: string) => {
    const base: CreateTaskInput =
      mode === 'mine'
        ? { title, dueAt: dueForBucket(group.key as Parameters<typeof dueForBucket>[0], null) ?? null, projectId: scope.current?.id ?? null }
        : { title, assigneeUserId: null, ...(group.key === 'none' ? { projectId: scope.current?.id ?? null } : { sectionId: group.key }) };
    return create.mutateAsync(base).catch(fail);
  };

  const viewProps: TaskViewProps = {
    groups,
    tree,
    draggable: mode === 'mine' || sort === 'manual',
    onMove,
    selection,
    onOpen: (id) => drawer.set(id),
    onQuickAdd: quickAdd,
    showProject,
    parents,
    sort: effectiveSort,
    onSort: setSort,
    footer:
      mode === 'project' ? (
        <Button type="dashed" icon={<PlusOutlined />} onClick={() => setSectionModal(true)} block={view === 'board'}>
          Add section
        </Button>
      ) : undefined,
  };

  const title = mode === 'mine' ? 'My tasks' : 'Tasks';
  const subtitle =
    mode === 'mine'
      ? `Tasks assigned to ${user?.name ?? 'you'}${scope.current ? ` in ${scope.current.name}` : ''}, by due date.`
      : scope.current
        ? `Tasks of ${scope.current.name}.`
        : scope.enabled
          ? 'Tasks across all projects. Pick a project in the header to see one.'
          : 'Plan, assign and track work.';

  return (
    <div className="page">
      <PageHeader
        title={title}
        subtitle={subtitle}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setTaskModal(true)}>
            Add task
          </Button>
        }
      />
      <Card>
        <TaskToolbar
          view={view}
          onView={setView}
          filters={filters}
          onFilters={setFilters}
          sort={sort}
          onSort={setSort}
          status={status}
          onStatus={setStatus}
          hideAssignee={mode === 'mine'}
        />
        {ws.isError ? (
          <LoadError error={ws.error} onRetry={ws.refetch} title="Could not load tasks" />
        ) : !ws.data ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : view === 'list' ? (
          <TaskListView {...viewProps} />
        ) : view === 'board' ? (
          <TaskBoardView {...viewProps} />
        ) : (
          <TaskCalendarView
            tasks={filterFlat(tasks, filters).sort(compareTasks('manual'))}
            undatedCount={tree.roots.filter((t) => !t.dueAt).length}
            showProject={showProject}
            onOpen={(id) => drawer.set(id)}
            onReschedule={(t, day) => update.mutate({ id: t.id, dueAt: dueFromDay(day) }, { onError: fail })}
            onQuickAdd={(day, t) =>
              create.mutateAsync({ title: t, dueAt: dueFromDay(day), projectId: scope.current?.id ?? null, ...(mode === 'project' ? { assigneeUserId: null } : {}) }).catch(fail)
            }
          />
        )}
      </Card>

      <TaskBulkBar ids={[...selection.ids]} onClear={selection.clear} sections={mode === 'project' ? sections : undefined} />
      <SectionModal open={sectionModal} onClose={() => setSectionModal(false)} defaultProject={scope.current?.id ?? null} />
      <NewTaskModal open={taskModal} onClose={() => setTaskModal(false)} mode={mode} sections={sections} onCreated={(t) => drawer.set(t.id)} />
      <Drawer
        open={!!drawer.open}
        onClose={() => drawer.set(null)}
        size={760}
        closable={false}
        destroyOnHidden
        styles={{ body: { padding: 0 } }}
      >
        {drawer.open && <TaskDetailPanel taskId={drawer.open} onOpen={(id) => drawer.set(id)} onClose={() => drawer.set(null)} fullPageLink />}
      </Drawer>
    </div>
  );
}

export const TasksPage = () => <TaskWorkspacePage key="project" mode="project" />;
export const MyTasksPage = () => <TaskWorkspacePage key="mine" mode="mine" />;
